import { useParams, useNavigate } from "react-router";
import { useState } from "react";
import { KnownProduct } from "../Product/KnownProduct.tsx";
import { ErrorPopup } from "../../UI/Error/ErrorPopup.tsx";
import {
  CreateNormasTeachingProductForm,
  CreateNormasTeachingProductFormValues,
} from "./CreateNormasTeachingProductForm.tsx";
import { FullPageLoading } from "../../UI/FullPageLoading.tsx";
import { useKnownPurchaseAcTags } from "../../ProductService/useKnownPurchaseAcTags.ts";
import { useKnownRefundAcTags } from "../../ProductService/useKnownRefundAcTags.ts";
import { useKnownAnalyticsTags } from "../../ProductService/useKnownAnalyticsTags.ts";
import { AdminAllNormasTeachingProductScreenPath } from "./AdminAllNormasTeachingProductScreen.tsx";
import { useUpdateNormasTeachingProduct } from "../../ProductService/NormasTeachingProduct/useUpdateNormasTeachingProduct.ts";
import { useAllNormasTeachingProducts } from "../../ProductService/NormasTeachingProduct/useAllNormasTeachingProducts.ts";
import { useNormasTeachingProductById } from "../../ProductService/NormasTeachingProduct/useNormasTeachingProductById.ts";

export const EditNormasTeachingProductScreenPath =
  "/admin/normasteaching/edit-product/:productId";

export function EditNormasTeachingProductScreen() {
  const { productId } = useParams<{ productId: string }>();

  const { product, isLoading: loadingProduct } = useNormasTeachingProductById(
    productId!,
  );

  const { products } = useAllNormasTeachingProducts();
  const { isLoading: isLoadingPurchaseTags, purchaseTags } =
    useKnownPurchaseAcTags("products");
  const { isLoading: isLoadingRefundTags, refundTags } =
    useKnownRefundAcTags("products");
  const { isLoading: isLoadingAnalyticsTags, analyticsTags } =
    useKnownAnalyticsTags("products");

  const navigate = useNavigate();

  const { updateNormasTeachingProduct, loading: updating } =
    useUpdateNormasTeachingProduct();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onSubmit = async (data: CreateNormasTeachingProductFormValues) => {
    if (!productId) return;

    try {
      setErrorMessage(null);
      await updateNormasTeachingProduct(productId, {
        productName: data.productName,
        variantProductName: data.variantProductName,
        productClass: data.productClass,
        priceInCents: { amount: data.priceInEuros * 100, currency: "EUR" },
        bundledProductIds: data.bundledProductIds ?? [],
        purchaseAcTags: data.purchaseAcTags ?? [],
        refundAcTags: data.refundAcTags ?? [],
        peopleCount: data.peopleCount,
        fpsProductInfo:
          data.productClass === KnownProduct.FPS
            ? {
                durationInWeeks: data.fpsLessonsDurationInWeeks || 0,
                unlimitedLessonsPerWeek:
                  data.fpsUnlimitedLessonsPerWeek || false,
                lessonsPerWeek: data.fpsLessonsPerWeek || 0,
                totalLessons: data.fpsTotalLessons || 0,
                extraWeek: data.fpsExtraWeek || false,
                warrantyEnabled: data.fpsWarrantyEnabled || false,
                warrantyMinAttendedLessons:
                  data.fpsWarrantyMinAttendedLessons || 0,
                userGroupSlug: data.userGroupSlug,
                doNotEnroll: data.doNotEnroll,
                doNotMerge: data.oneOnOne ? false : data.doNotMerge,
                oneOnOne: data.oneOnOne,
                oneOnOneTotalLessons: data.oneOnOneTotalLessons ?? 0,
                oneOnOneDurationInWeeks: data.oneOnOneDurationInWeeks ?? 0,
                oneOnOneMaxCancellations: data.oneOnOneMaxCancellations ?? 0,
                oneOnOneLessonType: data.oneOnOneLessonType,
              }
            : undefined,
        analyticsTag: data.analyticsTag,
        stripeConfig: data.stripeConfig,
        heylightConfig: data.heylightConfig,
        productDescription: data.productDescription,
        isCouponEnabled: data.isCouponEnabled,
      });
      window.location.href = AdminAllNormasTeachingProductScreenPath;
    } catch (error) {
      console.error("Errore durante l'aggiornamento del prodotto:", error);
      setErrorMessage("Errore durante l'aggiornamento del prodotto. Riprova.");
    }
  };

  if (
    loadingProduct ||
    isLoadingPurchaseTags ||
    isLoadingRefundTags ||
    isLoadingAnalyticsTags
  ) {
    return <FullPageLoading />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <ErrorPopup
        open={errorMessage !== null}
        title="Errore"
        description={errorMessage ?? ""}
        onClose={() => setErrorMessage(null)}
      />
      <div className="mb-6">
        <button
          onClick={() => navigate(AdminAllNormasTeachingProductScreenPath)}
          className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
        >
          ← Torna alla lista prodotti
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-6">Modifica Prodotto</h1>

        <CreateNormasTeachingProductForm
          initialKnownPurchaseAcTags={purchaseTags}
          initialKnownRefundAcTags={refundTags}
          initialKnownAnalyticsTags={analyticsTags}
          products={products}
          creating={updating}
          onSubmit={onSubmit}
          initialProduct={product}
          isEditMode={true}
        />
      </div>
    </div>
  );
}
